import React, {
  type ComponentProps,
  forwardRef,
  useCallback,
  useRef,
} from 'react';
import {
  Pressable,
  type PressableStateCallbackType,
  type StyleProp,
  StyleSheet,
  type View,
  type ViewStyle,
} from 'react-native';
import useMergedRef from '@react-hook/merged-ref';
import { theme } from '@garnish/constants';

import { webOnlyStyles } from '../../../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientCardPressable = forwardRef<View, Props>((props, ref) => {
  const {
    style,
    disabled,
    accessibilityLabel,
    isNestedElementFocused,
    ...restProps
  } = props;

  const localRef = useRef<View>(null);
  const mergedRef = useMergedRef(localRef, ref);

  // ─── Styles ──────────────────────────────────────────────────────────

  const pressableStyles = useCallback(
    (state: PressableStateCallbackType): StyleProp<ViewStyle> => {
      const { pressed } = state;

      return [
        styles.pressable,
        pressableWebStyles,
        isNestedElementFocused ? undefined : styles.pressableOnTop,
        pressed && !disabled ? styles.pressablePressed : undefined,
        style,
      ];
    },
    [disabled, isNestedElementFocused, style],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      ref={mergedRef}
      accessibilityLabel={accessibilityLabel}
      role="button"
      style={pressableStyles}
      disabled={disabled}
      {...restProps}
    />
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: theme.radius.large,
  },
  pressableOnTop: {
    zIndex: 1,
  },
  pressableHover: {
    backgroundColor: theme.colors.OPACITY.WHITE.DARKEST,
  },
  pressablePressed: {
    backgroundColor: theme.colors.OPACITY.WHITE.DARKER,
  },
});

const pressableWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
  transition: theme.transitions.presets.colorsAndOpacity,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  style?: ViewStyle;
  accessibilityLabel?: string;
  isNestedElementFocused?: boolean;
} & Omit<ComponentProps<typeof Pressable>, 'style'>;
